'use client'

import React, { useState, useEffect, useCallback, useTransition } from 'react'
import {
  getAllFeatureFlags,
  getFeatureFlagById,
  createFeatureFlag,
  updateFeatureFlag,
  deleteFeatureFlag,
  addUserToFeatureFlag,
  removeUserFromFeatureFlag,
  searchUsersForFeatureFlag,
  FeatureFlag,
  SearchUserResult,
} from '@/features/feature-flags/api/feature-flags.api'
import { useFeatureFlags } from '@/features/feature-flags/hooks/use-feature-flag'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { Switch } from '@/shared/components/ui/switch'
import { Badge } from '@/shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import {
  Plus,
  Users,
  Search,
  Trash2,
  Check,
  X,
  SlidersHorizontal,
  Loader2,
  AlertCircle,
  Flag,
} from 'lucide-react'
import { toast } from 'sonner'

export function FeatureFlagsTab() {
  const { refreshFlags: refreshAppFlags } = useFeatureFlags()
  const [flags, setFlags] = useState<FeatureFlag[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Create Flag Modal state
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [createKey, setCreateKey] = useState('')
  const [createName, setCreateName] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [createIsEnabled, setCreateIsEnabled] = useState(true)
  const [createEnabledForAll, setCreateEnabledForAll] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  // Manage Users Modal state
  const [activeFlagForUsers, setActiveFlagForUsers] = useState<FeatureFlag | null>(null)
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false)
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchUserResult[]>([])
  const [isSearchingUsers, setIsSearchingUsers] = useState(false)
  const [togglingUserId, setTogglingUserId] = useState<number | null>(null)

  // Delete Flag Modal state
  const [flagToDelete, setFlagToDelete] = useState<FeatureFlag | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Load flags
  const loadFlags = useCallback(async () => {
    try {
      setIsLoading(true)
      const res = await getAllFeatureFlags()
      if (res.success && res.data) {
        setFlags(res.data)
      } else {
        toast.error((res as any)?.message || 'Failed to load feature flags')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error loading feature flags')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadFlags()
  }, [loadFlags])

  // Toggle Master Enable Switch
  const handleToggleMaster = async (flag: FeatureFlag, newEnabled: boolean) => {
    try {
      // Optimistic update
      setFlags((prev) =>
        prev.map((f) => (f.id === flag.id ? { ...f, isEnabled: newEnabled } : f)),
      )
      const res = await updateFeatureFlag(flag.id, { isEnabled: newEnabled })
      if (!res.success) {
        // Rollback
        setFlags((prev) =>
          prev.map((f) => (f.id === flag.id ? { ...f, isEnabled: !newEnabled } : f)),
        )
        toast.error((res as any)?.message || 'Failed to update feature flag')
      } else {
        toast.success(
          `Feature flag "${flag.name}" is now ${newEnabled ? 'Enabled' : 'Disabled'}`,
        )
        refreshAppFlags()
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error updating feature flag')
      loadFlags()
    }
  }

  // Toggle Rollout to 100% of Users Switch
  const handleToggleRolloutAll = async (flag: FeatureFlag, newRollout: boolean) => {
    try {
      setFlags((prev) =>
        prev.map((f) =>
          f.id === flag.id ? { ...f, enabledForAll: newRollout } : f,
        ),
      )
      const res = await updateFeatureFlag(flag.id, { enabledForAll: newRollout })
      if (!res.success) {
        setFlags((prev) =>
          prev.map((f) =>
            f.id === flag.id ? { ...f, enabledForAll: !newRollout } : f,
          ),
        )
        toast.error((res as any)?.message || 'Failed to update rollout status')
      } else {
        toast.success(
          newRollout
            ? `"${flag.name}" is now rolled out to 100% of users`
            : `"${flag.name}" is now restricted to targeted users`,
        )
        refreshAppFlags()
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error updating rollout')
      loadFlags()
    }
  }

  // Create Flag
  const handleCreateFlag = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!createKey.trim() || !createName.trim()) {
      toast.error('Key and name are required')
      return
    }

    try {
      setIsCreating(true)
      const res = await createFeatureFlag({
        key: createKey.trim().toLowerCase(),
        name: createName.trim(),
        description: createDescription.trim() || undefined,
        isEnabled: createIsEnabled,
        enabledForAll: createEnabledForAll,
      })

      if (res.success && res.data) {
        toast.success(`Feature flag "${res.data.name}" created successfully`)
        setIsCreateOpen(false)
        setCreateKey('')
        setCreateName('')
        setCreateDescription('')
        setCreateIsEnabled(true)
        setCreateEnabledForAll(false)
        await loadFlags()
        refreshAppFlags()
      } else {
        toast.error((res as any)?.message || 'Failed to create feature flag')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error creating feature flag')
    } finally {
      setIsCreating(false)
    }
  }

  // Open Manage Users Dialog
  const handleOpenUsersModal = async (flag: FeatureFlag) => {
    setActiveFlagForUsers(flag)
    setIsUsersModalOpen(true)
    setUserSearchQuery('')
    setSearchResults([])

    // Refetch detailed flag with assigned users
    try {
      const res = await getFeatureFlagById(flag.id)
      if (res.success && res.data) {
        setActiveFlagForUsers(res.data)
      }
    } catch (err) {
      console.error('Error fetching flag details:', err)
    }

    // Pre-populate search with top users
    searchUsers('')
  }

  // Search users with debounce
  const searchUsers = async (query: string) => {
    try {
      setIsSearchingUsers(true)
      const res = await searchUsersForFeatureFlag(query)
      if (res.success && res.data) {
        setSearchResults(res.data)
      }
    } catch (err) {
      console.error('Error searching users:', err)
    } finally {
      setIsSearchingUsers(false)
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setUserSearchQuery(val)
    searchUsers(val)
  }

  // Toggle user into / out of feature flag
  const handleToggleUser = async (userId: number, isCurrentlyAssigned: boolean) => {
    if (!activeFlagForUsers) return
    try {
      setTogglingUserId(userId)
      if (isCurrentlyAssigned) {
        const res = await removeUserFromFeatureFlag(activeFlagForUsers.id, userId)
        if (res.success && res.data) {
          setActiveFlagForUsers(res.data)
          toast.success('User removed from feature')
          refreshAppFlags()
          loadFlags()
        } else {
          toast.error((res as any)?.message || 'Failed to remove user')
        }
      } else {
        const res = await addUserToFeatureFlag(activeFlagForUsers.id, userId)
        if (res.success && res.data) {
          setActiveFlagForUsers(res.data)
          toast.success('User enabled for feature')
          refreshAppFlags()
          loadFlags()
        } else {
          toast.error((res as any)?.message || 'Failed to enable user')
        }
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error toggling user')
    } finally {
      setTogglingUserId(null)
    }
  }

  // Delete Flag
  const handleDeleteFlag = async () => {
    if (!flagToDelete) return
    try {
      setIsDeleting(true)
      const res = await deleteFeatureFlag(flagToDelete.id)
      if (res.success) {
        toast.success(`Feature flag "${flagToDelete.name}" deleted`)
        setFlagToDelete(null)
        await loadFlags()
        refreshAppFlags()
      } else {
        toast.error((res as any)?.message || 'Failed to delete feature flag')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error deleting feature flag')
    } finally {
      setIsDeleting(false)
    }
  }

  const assignedUserIds = new Set(
    (activeFlagForUsers?.users || []).map((u) => u.id),
  )

  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Feature Flags</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Control feature rollouts and toggle specific users to receive new features like the Job Board.
          </p>
        </div>
        <Button
          onClick={() => setIsCreateOpen(true)}
          className="bg-primary hover:bg-primary/90 text-white font-medium gap-2 shrink-0"
        >
          <Plus className="h-4 w-4" />
          Create Feature Flag
        </Button>
      </div>

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : flags.length === 0 ? (
        <div className="rounded-xl border border-dashed p-10 text-center space-y-3">
          <Flag className="h-10 w-10 text-muted-foreground mx-auto" />
          <p className="font-medium text-base">No feature flags found</p>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            Get started by creating your first feature flag to control rollouts.
          </p>
          <Button
            onClick={() => setIsCreateOpen(true)}
            variant="outline"
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Create Feature Flag
          </Button>
        </div>
      ) : (
        /* Flags list */
        <div className="grid grid-cols-1 gap-4">
          {flags.map((flag) => {
            const userCount =
              flag.userCount !== undefined
                ? flag.userCount
                : (flag.users?.length ?? 0)

            return (
              <div
                key={flag.id}
                className="rounded-xl border bg-card p-5 shadow-sm space-y-4 hover:border-primary/40 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="space-y-1.5 max-w-xl">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <h3 className="font-semibold text-lg text-foreground">
                        {flag.name}
                      </h3>
                      <code className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">
                        {flag.key}
                      </code>
                      {flag.isEnabled ? (
                        <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          Inactive (Master Off)
                        </Badge>
                      )}
                    </div>
                    {flag.description && (
                      <p className="text-sm text-muted-foreground">
                        {flag.description}
                      </p>
                    )}
                  </div>

                  {/* Actions right */}
                  <div className="flex items-center gap-2 shrink-0 self-end md:self-start">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenUsersModal(flag)}
                      className="gap-1.5"
                    >
                      <Users className="h-4 w-4" />
                      Manage Users
                      <span className="ml-1 rounded-full bg-primary/10 text-primary px-1.5 py-0.2 text-xs font-semibold">
                        {userCount}
                      </span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setFlagToDelete(flag)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      title="Delete flag"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Control switches bar */}
                <div className="pt-3 border-t flex flex-wrap items-center justify-between gap-6 text-sm">
                  {/* Master Switch */}
                  <div className="flex items-center gap-3">
                    <Switch
                      id={`master-switch-${flag.id}`}
                      checked={flag.isEnabled}
                      onCheckedChange={(checked) =>
                        handleToggleMaster(flag, checked)
                      }
                    />
                    <label
                      htmlFor={`master-switch-${flag.id}`}
                      className="cursor-pointer font-medium"
                    >
                      Master Enabled Switch
                    </label>
                  </div>

                  {/* Rollout mode */}
                  <div className="flex items-center gap-3">
                    <Switch
                      id={`rollout-switch-${flag.id}`}
                      checked={flag.enabledForAll}
                      disabled={!flag.isEnabled}
                      onCheckedChange={(checked) =>
                        handleToggleRolloutAll(flag, checked)
                      }
                    />
                    <label
                      htmlFor={`rollout-switch-${flag.id}`}
                      className={`cursor-pointer font-medium ${
                        !flag.isEnabled ? 'text-muted-foreground' : ''
                      }`}
                    >
                      Roll out to 100% of users
                    </label>
                  </div>

                  {/* Current Status Pill */}
                  <div className="ml-auto text-xs">
                    {!flag.isEnabled ? (
                      <span className="text-muted-foreground font-medium">
                        Feature is completely disabled
                      </span>
                    ) : flag.enabledForAll ? (
                      <span className="text-emerald-600 font-semibold flex items-center gap-1">
                        <Check className="h-3.5 w-3.5" /> All authenticated users receive this feature
                      </span>
                    ) : (
                      <span className="text-amber-600 font-semibold flex items-center gap-1">
                        <SlidersHorizontal className="h-3.5 w-3.5" /> Selective rollout: {userCount} targeted user{userCount === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* CREATE FLAG DIALOG */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Feature Flag</DialogTitle>
            <DialogDescription>
              Add a new feature flag to toggle rollout for upcoming releases.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateFlag} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Display Name *</label>
              <Input
                placeholder="e.g. Job Board"
                value={createName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const val = e.target.value
                  setCreateName(val)
                  if (!createKey || createKey === createName.toLowerCase().replace(/[^a-z0-9]+/g, '_')) {
                    setCreateKey(val.toLowerCase().replace(/[^a-z0-9]+/g, '_'))
                  }
                }}
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Flag Key (Slug) *</label>
              <Input
                placeholder="e.g. job_board"
                value={createKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCreateKey(
                    e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''),
                  )
                }
                required
              />
              <p className="text-xs text-muted-foreground">
                Unique identifier used in code (e.g. <code>job_board</code>)
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                placeholder="What does this feature flag do?"
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                rows={2}
              />
            </div>

            <div className="pt-2 border-t space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium block">
                    Master Enabled
                  </label>
                  <span className="text-xs text-muted-foreground">
                    Activate the feature flag system for this feature
                  </span>
                </div>
                <Switch
                  checked={createIsEnabled}
                  onCheckedChange={setCreateIsEnabled}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium block">
                    Roll out to 100% of users
                  </label>
                  <span className="text-xs text-muted-foreground">
                    If off, only explicitly assigned users will receive it
                  </span>
                </div>
                <Switch
                  checked={createEnabledForAll}
                  onCheckedChange={setCreateEnabledForAll}
                />
              </div>
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateOpen(false)}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Flag'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* MANAGE USERS DIALOG */}
      <Dialog open={isUsersModalOpen} onOpenChange={setIsUsersModalOpen}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Manage Users for "{activeFlagForUsers?.name}"
            </DialogTitle>
            <DialogDescription>
              Toggle specific users to receive this feature flag. When "Roll out to 100% of users" is disabled, only toggled users can access this feature.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 flex-1 overflow-y-auto">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search user by name or email..."
                value={userSearchQuery}
                onChange={handleSearchChange}
                className="pl-9"
              />
              {isSearchingUsers && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>

            {/* Currently Assigned Summary */}
            {activeFlagForUsers?.users && activeFlagForUsers.users.length > 0 && (
              <div className="rounded-lg bg-muted/40 p-3 space-y-2 border">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Currently Enabled Users ({activeFlagForUsers.users.length})
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto pr-1">
                  {activeFlagForUsers.users.map((u) => (
                    <span
                      key={u.id}
                      className="inline-flex items-center gap-1.5 bg-background border px-2.5 py-1 rounded-full text-xs font-medium shadow-xs"
                    >
                      <span>{u.fullname || u.email}</span>
                      <button
                        type="button"
                        onClick={() => handleToggleUser(u.id, true)}
                        className="text-muted-foreground hover:text-destructive transition-colors ml-0.5"
                        title="Remove user"
                        disabled={togglingUserId === u.id}
                      >
                        {togglingUserId === u.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* User Search & Selection List */}
            <div className="space-y-1 pt-1">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Users
              </div>

              {searchResults.length === 0 && !isSearchingUsers ? (
                <div className="text-center py-6 text-sm text-muted-foreground">
                  No users found matching your search.
                </div>
              ) : (
                <div className="divide-y border rounded-lg max-h-64 overflow-y-auto">
                  {searchResults.map((user) => {
                    const isAssigned = assignedUserIds.has(user.id)
                    const isBusy = togglingUserId === user.id

                    return (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-3 hover:bg-muted/30 transition-colors"
                      >
                        <div className="space-y-0.5 min-w-0 pr-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-foreground truncate">
                              {user.fullname || 'No Name'}
                            </span>
                            {user.role && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-normal">
                                {user.role}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {user.email || 'No email'}
                          </p>
                        </div>

                        <div className="shrink-0 flex items-center gap-2">
                          <Button
                            size="sm"
                            variant={isAssigned ? 'default' : 'outline'}
                            onClick={() => handleToggleUser(user.id, isAssigned)}
                            disabled={isBusy}
                            className={`h-8 gap-1.5 text-xs ${
                              isAssigned
                                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                : ''
                            }`}
                          >
                            {isBusy ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : isAssigned ? (
                              <>
                                <Check className="h-3.5 w-3.5" />
                                Enabled
                              </>
                            ) : (
                              <>
                                <Plus className="h-3.5 w-3.5" />
                                Enable
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="pt-3 border-t">
            <Button onClick={() => setIsUsersModalOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={!!flagToDelete}
        onOpenChange={(open) => !open && setFlagToDelete(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Delete Feature Flag
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the feature flag "{flagToDelete?.name}" (
              <code>{flagToDelete?.key}</code>)? Any feature gated by this flag will evaluate to disabled. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              onClick={() => setFlagToDelete(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteFlag}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete Flag'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default FeatureFlagsTab
